import React, { useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { transcribeAudio } from "../api";
import { useLang } from "../i18n";
import { toast } from "sonner";

export function VoiceButton({ onResult, compact = false }) {
  const { t, lang } = useLang();
  const [state, setState] = useState("idle"); // idle | recording | transcribing
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        streamRef.current.getTracks().forEach((tr) => tr.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setState("transcribing");
        try {
          const text = await transcribeAudio(blob, lang);
          if (text) onResult(text);
          else toast.error(lang === "it" ? "Non ho capito, riprova" : "Didn't catch that, retry");
        } catch (e) {
          toast.error(lang === "it" ? "Errore trascrizione" : "Transcription error");
        } finally {
          setState("idle");
        }
      };
      mr.start();
      recRef.current = mr;
      setState("recording");
    } catch (e) {
      toast.error(lang === "it" ? "Microfono non disponibile" : "Microphone unavailable");
    }
  };

  const stop = () => recRef.current && recRef.current.stop();

  const onClick = state === "recording" ? stop : state === "idle" ? start : undefined;
  const icon = state === "transcribing" ? <Loader2 className="w-4 h-4 animate-spin" /> : state === "recording" ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />;

  if (compact) {
    return (
      <button
        type="button"
        data-testid="voice-button"
        onClick={onClick}
        title={t("dictate_field")}
        aria-label={t("dictate_field")}
        className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
          state === "recording" ? "bg-[#C81D6B] text-white animate-pulse" : "bg-[#EAF0EC] text-[#3F6E85] hover:bg-[#dbe7df]"
        }`}
      >
        {icon}
      </button>
    );
  }

  const label = state === "recording" ? t("recording") : state === "transcribing" ? t("transcribing") : t("record");
  return (
    <button
      type="button"
      data-testid="voice-button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
        state === "recording" ? "bg-[#C81D6B] text-white animate-pulse" : "bg-[#EAF0EC] text-[#3F6E85] hover:bg-[#dbe7df]"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}
